import access from "csi/access/_base";
import configPreferences from "csi/config/preferences";
import { View } from "csi/def/View";
import { C8Node } from "csi/typings";

export const DEFAULT_FORM_ID = "__Form__";
export const VIEW_TYPE_FORM = "Form";
export const VIEW_TYPE_GALLERY = "Gallery";
export const VIEW_TYPE_REACT = "React";
export const VIEW_TYPE_TABS = "Tabs";
export const ANGLE_SEPARATOR = " > ";
export const DASH_SEPARATOR = " - ";

/**
 * Return composition of viewId with subtypeName if provided
 */
export function composeViewIdWithSubtype(viewId: string, subtypeName = ""): string {
	return subtypeName ? `${viewId}_${subtypeName}` : viewId;
}

/**
 * Return key for view given components
 * @param viewId
 * @param typeNode
 * @param [context]
 * @param [isGallery] - if true, used to compose the key for gallery view
 */
export function composeViewKey(viewId: string, typeNode: string, context?: string, isGallery?: boolean): string {
	if (isGallery) {
		context = (context || "") + "|" + VIEW_TYPE_GALLERY;
	}

	return viewId + "|" + typeNode + (context ? ("|" + context) : "");
}

/**
 * Return components of view key
 */
export function decomposeViewKey(key: string): any {
	const a = key.split("|");
	const isGallery = (a[3] === VIEW_TYPE_GALLERY);

	return {
		id: a[0],
		type: a[1],
		context: a[2],
		isGallery,
	};
}

/**
 * Determine if user has access to view on given type, and optionally for the given node;
 * if node not provided, then global roles of user are used to test access rather than contextual roles
 * @param subtypeName subtype name to be combined with view id if provided
 */
export function getViewAccessRights(locationNodeType: string, viewNodeType: string, viewId: string, subtypeName?: string, node?: C8Node): number {
	// TODO: Move to csi/service/viewService once no longer needed in csi/def
	const {
		RESOURCE_FREE_ACCESS,
		RESOURCE_NO_ACCESS,
		RESOURCE_READ_ONLY_ACCESS,
	} = access;

	const accessRight = node ? access.getViewAccess(viewNodeType, viewId, node) : RESOURCE_FREE_ACCESS;

	if (!accessRight) {
		return RESOURCE_NO_ACCESS;
	}

	const id = composeViewIdWithSubtype(viewId, subtypeName);
	const key = composeViewKey(id, viewNodeType, locationNodeType);

	if (configPreferences.isViewHidden(key)) {
		return RESOURCE_NO_ACCESS;
	}

	return configPreferences.isViewReadOnly(key) ? RESOURCE_READ_ONLY_ACCESS : accessRight;
}

/**
 * Return true if view is a react component
 */
export function isReactView(defView: View): boolean {
	return defView.m_typeView === VIEW_TYPE_REACT;
}
